import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, LoadScript, DrawingManager, Libraries, Marker, Autocomplete } from '@react-google-maps/api';
import { Box, Paper, Typography, IconButton, InputBase, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ToolMode } from './BottomRightTools';
import type { LatLng, ArrowDescriptor, ArrowDraft, ArrowInput } from '../types/arrows';
import { RoadToolOverlay } from './RoadToolOverlay';
import { Generate } from './Generate';


import logo from '../assets/logo.svg';
import { Visibility } from '@mui/icons-material';

const libraries: Libraries = ["drawing", "places", "geometry"];

const toRadians = (deg: number) => (deg * Math.PI) / 180;
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000; // meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const bearingBetween = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const phi1 = toRadians(lat1);
  const phi2 = toRadians(lat2);
  const dLon = toRadians(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const applyUniqueIds = (svgElement: SVGSVGElement, prefix: string) => {
  const idMap = new Map<string, string>();
  const classMap = new Map<string, string>();

  // Make IDs unique
  svgElement.querySelectorAll('[id]').forEach(el => {
    const oldId = el.getAttribute('id');
    if (!oldId) return;
    const newId = `${prefix}-${oldId}`;
    if (oldId === newId) return;
    idMap.set(oldId, newId);
    el.setAttribute('id', newId);
  });

  // Make CSS class names unique (THIS IS THE KEY FIX FOR COLORS!)
  svgElement.querySelectorAll('[class]').forEach(el => {
    const classList = el.getAttribute('class');
    if (!classList) return;
    const classes = classList.split(/\s+/).filter(c => c);
    const newClasses = classes.map(oldClass => {
      if (!classMap.has(oldClass)) {
        const newClass = `${prefix}-${oldClass}`;
        classMap.set(oldClass, newClass);
      }
      return classMap.get(oldClass)!;
    });
    el.setAttribute('class', newClasses.join(' '));
  });

  const updateValue = (value: string | null) => {
    if (!value) return value;
    let updated = value;
    idMap.forEach((newId, oldId) => {
      const oldHash = `#${oldId}`;
      const newHash = `#${newId}`;
      const urlRegex = new RegExp(`url\\(${escapeRegExp(oldHash)}\\)`, 'g');
      updated = updated.replace(urlRegex, `url(${newHash})`);

      const hashRegex = new RegExp(escapeRegExp(oldHash), 'g');
      updated = updated.replace(hashRegex, newHash);
    });
    return updated;
  };

  const attributeNames = [
    'fill',
    'stroke',
    'filter',
    'clip-path',
    'mask',
    'marker-start',
    'marker-mid',
    'marker-end',
    'style',
    'xlink:href',
    'href',
  ];

  svgElement.querySelectorAll('*').forEach(el => {
    attributeNames.forEach(name => {
      if (!el.hasAttribute(name)) return;
      const value = el.getAttribute(name);
      const updated = updateValue(value);
      if (updated !== value && updated != null) {
        el.setAttribute(name, updated);
      }
    });
  });

  // Update CSS class selectors in <style> tags
  svgElement.querySelectorAll('style').forEach(styleEl => {
    const cssText = styleEl.textContent;
    if (!cssText) return;
    let updatedCss = cssText;
    
    // Update ID references
    idMap.forEach((newId, oldId) => {
      const oldHash = `#${oldId}`;
      const newHash = `#${newId}`;
      const regex = new RegExp(escapeRegExp(oldHash), 'g');
      updatedCss = updatedCss.replace(regex, newHash);
    });
    
    classMap.forEach((newClass, oldClass) => {
      // Match .className but not .classname123 (word boundary)
      const regex = new RegExp(`\\.${escapeRegExp(oldClass)}(?![a-zA-Z0-9_-])`, 'g');
      updatedCss = updatedCss.replace(regex, `.${newClass}`);
    });
    
    styleEl.textContent = updatedCss;
  });
};

const defaultCenter = {
  lat: -33.8688, // Sydney
  lng: 151.2093,
};

// Standard TGP scale is typically 1:500 for urban areas
// At zoom level 18, the scale is approximately 1:2,257
// At zoom level 19, the scale is approximately 1:1,128
// At zoom level 20, the scale is approximately 1:564
// High-resolution container style for crisp rendering
const mapContainerStyle = {
  height: '100%',
  width: '100%',
  imageRendering: 'crisp-edges' as const, // Sharper edges for satellite imagery
  WebkitFontSmoothing: 'antialiased' as const, // Better text rendering
  transform: 'translateZ(0)', // Force GPU acceleration
  willChange: 'transform', // Optimize for transforms
};

const mapOptions = {
  zoom: 5, // Default zoom level (1/4 of original 21 for wide overview)
  center: defaultCenter,
  tilt: 10, // Sets the 3D tilt (0-45 degrees available, max is 45)
  heading: 95, // Rotation (45 = northeast up)
  // mapId: process.env.REACT_APP_GOOGLE_MAP_ID || undefined, // Disabled: when mapId is set, the styles array is ignored (cloud styling takes over)
  mapTypeId: 'hybrid' as const,

  // Quality & Performance Settings
  maxZoom: 23, // Maximum possible zoom level (absolute max for Google Maps)
  minZoom: 10, // Prevent zooming out too far
  isFractionalZoomEnabled: true, // Enable smooth, fractional zoom levels for better quality
  renderingType: 'VECTOR' as any, // Disabled: vector rendering ignores the styles array
  
  // High-DPI/Retina Support - Google Maps automatically uses higher resolution tiles on high-DPI displays
  // The API automatically detects window.devicePixelRatio and serves 2x tiles when available

  // UI Controls
  disableDefaultUI: true, // Disable all default UI
  zoomControl: true, // Keep only zoom controls
  scaleControl: false, // Remove scale control
  mapTypeControl: false, // Remove map type switcher
  streetViewControl: false, // Remove Street View pegman
  fullscreenControl: false, // Remove fullscreen button
  rotateControl: false, // Remove native rotation/tilt controls

  // Interaction Settings
  gestureHandling: 'greedy' as const, // Allows one-finger zoom on mobile
  clickableIcons: false, // Disable default map feature clicking
  keyboardShortcuts: false, // Disable keyboard shortcuts

  // Visual Settings - Street names enabled
  styles: [
    {
      featureType: 'all',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    }, 
    {
      featureType: 'road', 
      elementType: 'labels',
      stylers: [{visibility: 'on'}]
    }, 
    {
      featureType: 'administrative',
      elementType: 'labels',
      stylers: [{visibility: 'off'}]
    },
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{visibility: 'off'}]
    },
    {
      featureType: 'transit',
      elementType: 'labels',
      stylers: [{visibility: 'off'}]
    }
  ]
};

interface WorkzoneMapProps {
  apiKey: string | undefined;
  onWorkzoneSelect: (data: any) => void;
  overlays?: any | null;
  signPlacement?: any | null; // New prop for sign placement data
  cones?: any[] | null; // New prop for cone placement data
  signLibrary?: any | null;
  isManualMode?: boolean;
  onSignMove?: (signId: string, newLatLng: { lat: number; lng: number }) => void;
  onSignAdd?: (signCode: string, newLatLng: { lat: number; lng: number }, rotation?: number, composite?: any) => void;
  onSignRemove?: (signId: string) => void;
  onSignResize?: (signId: string, newScale: number) => void;
  onSignRotate?: (signId: string, newRotation: number) => void;
  onSignCalloutMove?: (signId: string, offset: { x: number; y: number }) => void;
  isDrawingPrintArea?: boolean;
  isDrawingWorkzone?: boolean;
  onPrintAreaSelect?: (bounds: any) => void;
  onMapReady?: (controls: {
    zoomIn: () => void;
    zoomOut: () => void;
    setDrawingMode: (mode: google.maps.drawing.OverlayType | null) => void;
    snapToLocation: (location?: { lat: number; lng: number }) => void;
    getMapInstance: () => google.maps.Map | null;
  }) => void;

  onAreaSelected?: (area: any) => void;
  onWorkzoneDrawingComplete?: () => void;
  currentTool?: ToolMode;
  arrowDraft?: ArrowDraft | null;
  arrows?: ArrowDescriptor[];
  onArrowDraftStart?: (point: LatLng) => void;
  onArrowComplete?: (arrow: ArrowInput) => void;
  onArrowDistanceChange?: (id: string, distance: number) => void;
  onArrowDelete?: (id: string) => void;
  workzones?: any[];
  roadToolGeometry?: any | null;
  roadToolVisible?: boolean;
  isProMode?: boolean;
}

export const WorkzoneMap: React.FC<WorkzoneMapProps> = ({
  apiKey,
  onWorkzoneSelect,
  overlays,
  signPlacement,
  cones,
  signLibrary,
  isManualMode = false,
  onSignMove,
  onSignAdd,
  onSignRemove,
  onSignResize,
  onSignRotate,
  isDrawingPrintArea = false,
  isDrawingWorkzone = false,
  onPrintAreaSelect,
  onMapReady,
  onAreaSelected,
  onWorkzoneDrawingComplete,
  currentTool = 'cursor',
  arrowDraft = null,
  arrows = [],
  onArrowDraftStart,
  onArrowComplete,
  onArrowDistanceChange,
  onArrowDelete,
  workzones = [],
  roadToolGeometry = null,
  roadToolVisible = false,
  isProMode = false,
}) => {
  const [map, setMap] = useState<any>(null);
  const [workzone, setWorkzone] = useState<any[]>([]); // Array of polygon objects for rendering
  const [activePolygon, setActivePolygon] = useState<any>(null); // Currently being drawn/edited
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [autocomplete, setAutocomplete] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [drawingManager, setDrawingManager] = useState<any>(null);
  const [city, setCity] = useState<string | null>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false); // Track drag over state
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchText, setSearchText] = useState('');
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  
  // Store InfoWindow and Marker references for persistent updates
  const infoWindowsRef = useRef<Map<string, google.maps.InfoWindow>>(new Map());
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const openInfoWindowRef = useRef<string | null>(null); // Track which InfoWindow is currently open
  useEffect(() => {
    if (!drawingManager) {
      console.warn('Drawing manager not ready yet');
      return;
    }

    const mode = isDrawingPrintArea
      ? window.google?.maps.drawing.OverlayType.RECTANGLE
      : isDrawingWorkzone
        ? window.google?.maps.drawing.OverlayType.POLYGON
        : null;

    if (map && drawingManager.getMap() !== map) {
      drawingManager.setMap(map);
    }

    drawingManager.setDrawingMode(mode);

    if (!map) {
      return;
    }

    map.setOptions({
      draggableCursor: mode ? 'crosshair' : undefined,
      draggingCursor: mode ? 'crosshair' : undefined,
    });

    const mapDiv = map.getDiv() as HTMLElement | null;
    if (mapDiv) {
      mapDiv.style.cursor = mode ? 'crosshair' : '';
    }
  }, [isDrawingPrintArea, isDrawingWorkzone, drawingManager, map]);


  // Add scale control when map loads

  
  const handleMapLoad = useCallback((map: any) => {
    setMap(map);
    
    
    // Position Google's zoom controls to bottom-right, but leave space for our tools
    if (window.google && window.google.maps) {
      setIsGoogleReady(true);
      map.setOptions({
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_BOTTOM
        }
      });
      setPlacesService(new window.google.maps.places.PlacesService(map));

      // Create Camera Control Panel for heading/tilt
      const cameraControlDiv = document.createElement('div');
      cameraControlDiv.style.margin = '10px';
      cameraControlDiv.style.backgroundColor = 'white';
      cameraControlDiv.style.borderRadius = '4px';
      cameraControlDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
      cameraControlDiv.style.padding = '8px';
      cameraControlDiv.style.fontFamily = 'Roboto, Arial, sans-serif';
      cameraControlDiv.style.fontSize = '12px';
      cameraControlDiv.style.minWidth = '200px';

      const infoDisplay = document.createElement('div');
      infoDisplay.style.marginBottom = '8px';
      infoDisplay.style.textAlign = 'center';
      infoDisplay.style.fontWeight = 'bold';
      infoDisplay.style.color = '#333';
      cameraControlDiv.appendChild(infoDisplay);

      const updateDisplay = () => {
        const heading = map.getHeading() || 0;
        const tilt = map.getTilt() || 0;
        const zoom = map.getZoom() || 0;
        infoDisplay.innerHTML = `${heading.toFixed(0)}° | ${tilt.toFixed(0)}° | ${zoom.toFixed(1)}`;
      };

      // Heading controls
      const headingRow = document.createElement('div');
      headingRow.style.display = 'flex';
      headingRow.style.gap = '4px';
      headingRow.style.marginBottom = '4px';
      headingRow.style.alignItems = 'center';

      const headingLabel = document.createElement('div');
      headingLabel.textContent = 'Heading:';
      headingLabel.style.width = '60px';
      headingLabel.style.fontSize = '11px';
      headingRow.appendChild(headingLabel);

      const headingLeftBtn = document.createElement('button');
      headingLeftBtn.textContent = '←';
      headingLeftBtn.style.flex = '1';
      headingLeftBtn.style.padding = '4px';
      headingLeftBtn.style.cursor = 'pointer';
      headingLeftBtn.style.border = '1px solid #ccc';
      headingLeftBtn.style.borderRadius = '3px';
      headingLeftBtn.style.backgroundColor = '#f8f8f8';
      headingRow.appendChild(headingLeftBtn);

      const headingResetBtn = document.createElement('button');
      headingResetBtn.textContent = '0°';
      headingResetBtn.style.flex = '1';
      headingResetBtn.style.padding = '4px';
      headingResetBtn.style.cursor = 'pointer';
      headingResetBtn.style.border = '1px solid #ccc';
      headingResetBtn.style.borderRadius = '3px';
      headingResetBtn.style.backgroundColor = '#f8f8f8';
      headingRow.appendChild(headingResetBtn);

      const headingRightBtn = document.createElement('button');
      headingRightBtn.textContent = '→';
      headingRightBtn.style.flex = '1';
      headingRightBtn.style.padding = '4px';
      headingRightBtn.style.cursor = 'pointer';
      headingRightBtn.style.border = '1px solid #ccc';
      headingRightBtn.style.borderRadius = '3px';
      headingRightBtn.style.backgroundColor = '#f8f8f8';
      headingRow.appendChild(headingRightBtn);

      cameraControlDiv.appendChild(headingRow);

      // Tilt controls
      const tiltRow = document.createElement('div');
      tiltRow.style.display = 'flex';
      tiltRow.style.gap = '4px';
      tiltRow.style.alignItems = 'center';

      const tiltLabel = document.createElement('div');
      tiltLabel.textContent = 'Tilt:';
      tiltLabel.style.width = '60px';
      tiltLabel.style.fontSize = '11px';
      tiltRow.appendChild(tiltLabel);

      const tiltDownBtn = document.createElement('button');
      tiltDownBtn.textContent = '↓';
      tiltDownBtn.style.flex = '1';
      tiltDownBtn.style.padding = '4px';
      tiltDownBtn.style.cursor = 'pointer';
      tiltDownBtn.style.border = '1px solid #ccc';
      tiltDownBtn.style.borderRadius = '3px';
      tiltDownBtn.style.backgroundColor = '#f8f8f8';
      tiltRow.appendChild(tiltDownBtn);

      const tiltResetBtn = document.createElement('button');
      tiltResetBtn.textContent = '0°';
      tiltResetBtn.style.flex = '1';
      tiltResetBtn.style.padding = '4px';
      tiltResetBtn.style.cursor = 'pointer';
      tiltResetBtn.style.border = '1px solid #ccc';
      tiltResetBtn.style.borderRadius = '3px';
      tiltResetBtn.style.backgroundColor = '#f8f8f8';
      tiltRow.appendChild(tiltResetBtn);

      const tiltUpBtn = document.createElement('button');
      tiltUpBtn.textContent = '↑';
      tiltUpBtn.style.flex = '1';
      tiltUpBtn.style.padding = '4px';
      tiltUpBtn.style.cursor = 'pointer';
      tiltUpBtn.style.border = '1px solid #ccc';
      tiltUpBtn.style.borderRadius = '3px';
      tiltUpBtn.style.backgroundColor = '#f8f8f8';
      tiltRow.appendChild(tiltUpBtn);

      cameraControlDiv.appendChild(tiltRow);

      // Event listeners
      headingLeftBtn.addEventListener('click', () => {
          const currentHeading = map.getHeading() || 0;
        const newHeading = (currentHeading - 15 + 360) % 360;
        map.setHeading(newHeading);
        updateDisplay();
      });

      headingResetBtn.addEventListener('click', () => {
        map.setHeading(0);
        updateDisplay();
      });

      headingRightBtn.addEventListener('click', () => {
        const currentHeading = map.getHeading() || 0;
        const newHeading = (currentHeading + 15) % 360;
        map.setHeading(newHeading);
        updateDisplay();
      });

      tiltDownBtn.addEventListener('click', () => {
        const currentTilt = map.getTilt() || 0;
        const newTilt = Math.max(0, currentTilt - 10);
        map.setTilt(newTilt);
        updateDisplay();
      });

      tiltResetBtn.addEventListener('click', () => {
        map.setTilt(0);
        updateDisplay();
      });

      tiltUpBtn.addEventListener('click', () => {
        const currentTilt = map.getTilt() || 0;
        const newTilt = Math.min(45, currentTilt + 10);
        map.setTilt(newTilt);
        updateDisplay();
      });

      // Update display when map camera changes
      map.addListener('heading_changed', updateDisplay);
      map.addListener('tilt_changed', updateDisplay);
      map.addListener('zoom_changed', updateDisplay);

      // Initial update
      updateDisplay();

      // Add to map
      map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(cameraControlDiv);
    }
    
    // Make remove sign function globally available for InfoWindow buttons
    if (onSignRemove) {
      (window as any).removeSign = (signId: string) => {
        onSignRemove(signId);
        // Close any open InfoWindows after removal
        const infoWindows = document.querySelectorAll('.gm-ui-hover-effect');
        infoWindows.forEach(iw => {
          const closeButton = iw.querySelector('[title="Close"]') as HTMLElement;
          if (closeButton) closeButton.click();
        });
      };
    }

    // Make resize sign function globally available for InfoWindow buttons
    // Define these functions once and update them via closure
    (window as any).resizeSign = (signId: string, newScale: number) => {
      if (onSignResize) {
        onSignResize(signId, newScale);
      }
    };
    
    // Handle custom scale input
    (window as any).applyCustomScale = (signId: string) => {
      const input = document.getElementById(`scale-input-${signId}`) as HTMLInputElement;
      if (onSignResize) {
        const value = parseFloat(input.value);
        if (!isNaN(value) && value >= 50 && value <= 300) {
          const scale = value / 100;
          onSignResize(signId, scale);
        } else {
          alert('Please enter a value between 50 and 300');
        }
      }
    };
    
    // Make rotate sign function globally available for InfoWindow buttons
    (window as any).rotateSign = (signId: string, newRotation: number) => {
      if (onSignRotate) {
        onSignRotate(signId, newRotation);
      }
    };
  }, [onSignRemove, onSignResize, onSignRotate]);

  // Update InfoWindows and markers when sign data changes (e.g., after resize or rotation)
  useEffect(() => {
    if (!signPlacement?.signs) return;
    
    // Update the currently open InfoWindow if there is one
    const openSignId = openInfoWindowRef.current;
    if (openSignId) {
      const sign = signPlacement.signs.find((s: any) => s.id === openSignId);
      const infoWindow = infoWindowsRef.current.get(openSignId);
      const marker = markersRef.current.get(openSignId);
      
      if (sign && infoWindow && marker) {
        // Update InfoWindow content with new data
        const newContent = generateInfoWindowContent(
          sign,
          isManualMode,
          !!onSignRemove,
          !!onSignResize
        );
        // Update marker icon with current scale and rotation first
        const rotation = sign.rotation !== undefined ? sign.rotation : 0;
        const isComposite = sign.sign.code_sa === 'COMPOSITE' && sign.composite;
        const newIcon = isComposite
          ? getCompositeSignIcon(sign.composite, sign.scale || 1.0, rotation)
          : getSignIcon(sign.sign.code_sa, sign.scale || 1.0, rotation);
        marker.setIcon(newIcon);
        
        // Update InfoWindow content and ensure it stays open
        infoWindow.setContent(newContent);
        infoWindow.open(map, marker);
      }
    }
  }, [signPlacement, isManualMode, onSignRemove, onSignResize, map]);

  const handlePlaceSelect = useCallback(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete || !map) return;
  
    const place = autocomplete.getPlace();
    if (!place.geometry?.location) return;
  
    setSearchText(place.formatted_address || place.name || '');
    setSelectedLocation(place.geometry.location);
    map.panTo(place.geometry.location);
    map.setZoom(19);
  
    const addressComponents = place.address_components;
    let cityName = null;
  
    if (addressComponents) {
      for (const component of addressComponents) {
        if (component.types.includes('locality')) {
          cityName = component.long_name;
          break;
        }
        if (component.types.includes('administrative_area_level_2')) {
          cityName = component.long_name;
        }
      }
    }
  
    setCity(cityName);
    onWorkzoneSelect({
      location: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      },
      address: place.formatted_address,
      name: place.name,
      city: cityName,
      state: 'SA',  // change to dynamically get the state from the place
      country:'Australia',  // change to dynamically get the country from the place
    });
  }, [map, onWorkzoneSelect]);
  

  const performTextSearch = useCallback(() => {
    if (!placesService || !searchText.trim() || !map) return;
    placesService.textSearch({ query: searchText }, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results[0]) {
        const location = results[0].geometry?.location;
        if (!location) return;
        setSelectedLocation(location);
        map.panTo(location);
        map.setZoom(19);
        const address = results[0].formatted_address || results[0].name;
        const name = results[0].name || results[0].formatted_address;
        const cityName = results[0].address_components?.find((comp) =>
          comp.types.includes('locality') || comp.types.includes('administrative_area_level_2')
        )?.long_name || null;
        setCity(cityName);
        onWorkzoneSelect({
          location: { lat: location.lat(), lng: location.lng() },
          address,
          name,
          city: cityName
        });
      }
    });
  }, [placesService, searchText, map, onWorkzoneSelect]);

  const handleSearchSubmit = useCallback(() => {
    // Prefer Autocomplete selection if available
    if (autocompleteRef.current && autocompleteRef.current.getPlace()) {
      handlePlaceSelect();
    } else {
      performTextSearch();
    }
  }, [handlePlaceSelect, performTextSearch]);

  // Render overlays into the Google Maps Data layer when available
  useEffect(() => {
    if (!map || !overlays || !window.google) return;
    const dataLayer = map.data as any;
    dataLayer.forEach((f: any) => dataLayer.remove(f));

    const redPointIcon: any = {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 4,
      fillColor: '#d32f2f',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 1,
    };
    const greenPointIcon: any = {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 3,
      fillColor: '#4caf50',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 1,
    };

    const addCollection = (fc: any, collectionName: string) => {
      if (!fc) return;
      try {
        const isFC = fc && typeof fc === 'object' && fc.type === 'FeatureCollection' && Array.isArray(fc.features);
        const isFeature = fc && typeof fc === 'object' && fc.type === 'Feature' && fc.geometry;
        if (!isFC && !isFeature) {
          console.warn(`Skipping invalid GeoJSON for ${collectionName}`, fc);
          return;
        }

        const tagged = isFC ? {
          type: fc.type,
          features: (fc.features || []).map((feat: any) => ({
            ...feat,
            properties: { ...(feat.properties || {}), collection: collectionName },
          })),
        } : {
          ...fc,
          properties: { ...(fc.properties || {}), collection: collectionName },
        };

        // Short-circuit empty collections
        if (tagged.type === 'FeatureCollection' && (!tagged.features || tagged.features.length === 0)) {
          return;
        }

        dataLayer.addGeoJson(tagged);
      } catch (e) {
        console.warn('Failed to add GeoJSON to map:', e);
      }
    };

    addCollection(overlays.edges_geojson, 'edges');
    addCollection(overlays.approaches_geojson, 'approaches');
    addCollection(overlays.intersections_geojson, 'intersections');
    addCollection(overlays.nodes_geojson, 'nodes');
    addCollection(overlays.traffic_flow_arrows, 'traffic_flow_arrows');

    dataLayer.setStyle((feature: any) => {
      const collection = feature.getProperty('collection');
      if (collection === 'edges') {
        return { strokeColor: '#1976d2', strokeWeight: 3 };
      }
      if (collection === 'approaches') {
        return { strokeColor: '#ff9800', strokeWeight: 3 };
      }
      if (collection === 'intersections') {
        return { icon: redPointIcon };
      }
      if (collection === 'nodes') {
        return { icon: greenPointIcon };
      }
      if (collection === 'traffic_flow_arrows') {
        return { icon: createTrafficFlowArrow(feature) };
      }
      return {};
    });

    // Function to create traffic flow arrow icons
    function createTrafficFlowArrow(feature: any) {
      const properties = feature.getProperty('bearing') !== undefined ? {
        bearing: feature.getProperty('bearing'),
        color: feature.getProperty('color') || '#1976d2',
        size: feature.getProperty('size') || 12,
        direction: feature.getProperty('direction') || 'forward',
        highway_type: feature.getProperty('highway_type') || 'unknown'
      } : {
        bearing: 0,
        color: '#1976d2',
        size: 12,
        direction: 'forward',
        highway_type: 'unknown'
      };

      // Create arrow symbol based on bearing
      const arrowIcon = {
        path: window.google?.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: properties.size / 3,
        fillColor: properties.color,
        fillOpacity: 0.8,
        strokeColor: '#ffffff',
        strokeWeight: 1,
        strokeOpacity: 0.8,
        rotation: properties.bearing
      };

      return arrowIcon;
    }

    // Fit bounds to overlays
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;
    const extendFromCoords = (coords: any) => {
      // coords may be [lng, lat] or nested list
      if (Array.isArray(coords) && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        bounds.extend({ lat: coords[1], lng: coords[0] });
        hasPoints = true;
      } else if (Array.isArray(coords)) {
        coords.forEach(extendFromCoords);
      }
    };
    const extendFromFC = (fc: any) => {
      if (!fc || !fc.features) return;
      fc.features.forEach((feat: any) => {
        const geom = feat.geometry;
        if (!geom) return;
        extendFromCoords(geom.coordinates);
      });
    };
    extendFromFC(overlays.edges_geojson);
    extendFromFC(overlays.approaches_geojson);
    extendFromFC(overlays.intersections_geojson);
    extendFromFC(overlays.nodes_geojson);
    
    // Also include sign placement data in bounds if available
    if (signPlacement && signPlacement.signs) {
      signPlacement.signs.forEach((sign: any) => {
        bounds.extend({ lat: sign.lat, lng: sign.lng });
        hasPoints = true;
      });
    }
    
    if (hasPoints) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }
  }, [map, overlays, signPlacement]);

  useEffect(() => {
    if (map && onMapReady) {
      onMapReady({
        zoomIn: () => map.setZoom(map.getZoom() + 1),
        zoomOut: () => map.setZoom(map.getZoom() - 1),
        setDrawingMode: (mode) => {
          if (drawingManager) {
            drawingManager.setDrawingMode(mode);
          }
        },
        snapToLocation: (location) => {
          const target = location || (selectedLocation && { lat: selectedLocation.lat(), lng: selectedLocation.lng() });
          if (target) {
            map.panTo(target);
            map.setZoom(19);
          }
        },
        getMapInstance: () => map
      });
    }
  }, [map, onMapReady, drawingManager, selectedLocation]);

  const activeArrowRef = useRef<google.maps.Polyline | null>(null);
  const previewLabelRef = useRef<google.maps.Marker | null>(null);
  const arrowPolylinesRef = useRef<Map<string, google.maps.Polyline>>(new Map());
  const arrowLabelsRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const arrowClickListenersRef = useRef<Map<string, google.maps.MapsEventListener>>(new Map());
  const arrowDraftRef = useRef<ArrowDraft | null>(arrowDraft);

  const formatDistance = useCallback((value: number) => {
    if (!Number.isFinite(value)) return '0';
    return Number.isInteger(value) ? `${value}` : value.toFixed(1);
  }, []);

  useEffect(() => {
    arrowDraftRef.current = arrowDraft;

    if (!arrowDraft?.start) {
      activeArrowRef.current?.setMap(null);
      activeArrowRef.current = null;
      previewLabelRef.current?.setMap(null);
      previewLabelRef.current = null;
    }
  }, [arrowDraft]);

  useEffect(() => {
    if (!map) return;

    const clickListener = map.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (currentTool !== 'arrow') return;
      if (!event.latLng) return;
      const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };

      const draftStart = arrowDraftRef.current?.start ?? null;

      if (!draftStart) {
        arrowDraftRef.current = { start: point };
        onArrowDraftStart?.(point);
        return;
      }

      const rawDistance = haversineDistance(draftStart.lat, draftStart.lng, point.lat, point.lng);
      const distance = Number.isFinite(rawDistance) ? Number(rawDistance.toFixed(1)) : 0;
      arrowDraftRef.current = null;
      onArrowComplete?.({ start: draftStart, end: point, distance });
      activeArrowRef.current?.setMap(null);
      activeArrowRef.current = null;
      previewLabelRef.current?.setMap(null);
      previewLabelRef.current = null;
    });

    const moveListener = map.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
      if (currentTool !== 'arrow') return;
      if (!event.latLng) return;
      const draftStart = arrowDraftRef.current?.start;
      if (!draftStart) return;

      const tempEnd = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      const distanceRaw = haversineDistance(draftStart.lat, draftStart.lng, tempEnd.lat, tempEnd.lng);
      const distance = Number.isFinite(distanceRaw) ? Number(distanceRaw.toFixed(1)) : 0;

      if (!activeArrowRef.current) {
        activeArrowRef.current = new google.maps.Polyline({
          strokeColor: '#1d4ed8',
          strokeOpacity: 0.7,
          strokeWeight: 2,
          clickable: false,
          icons: [
            {
              icon: {
                path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                scale: 2,
                strokeOpacity: 0.7,
                strokeColor: '#1d4ed8',
                fillColor: '#1d4ed8',
                fillOpacity: 0.7,
              },
              offset: '0%',
            },
            {
              icon: {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 2,
                strokeOpacity: 0.7,
                strokeColor: '#1d4ed8',
                fillColor: '#1d4ed8',
                fillOpacity: 0.7,
              },
              offset: '100%',
            },
          ],
        });
        activeArrowRef.current.setMap(map);
      }

      activeArrowRef.current.setPath([draftStart, tempEnd]);

      if (!previewLabelRef.current) {
        previewLabelRef.current = new google.maps.Marker({
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillOpacity: 0,
            strokeOpacity: 0,
          },
          label: {
            text: '',
            color: '#db2777',
            fontWeight: 'bold',
            fontSize: '16px',
          },
          zIndex: 500,
          clickable: false,
        });
        previewLabelRef.current.setMap(map);
      }

      previewLabelRef.current.setPosition({
        lat: (draftStart.lat + tempEnd.lat) / 2,
        lng: (draftStart.lng + tempEnd.lng) / 2,
      });
      previewLabelRef.current.setLabel({
        text: `${formatDistance(distance)} m`,
        color: '#db2777',
        fontWeight: 'bold',
        fontSize: '16px',
      });
    });

    return () => {
      arrowDraftRef.current = null;
      google.maps.event.removeListener(clickListener);
      google.maps.event.removeListener(moveListener);
      activeArrowRef.current?.setMap(null);
      activeArrowRef.current = null;
      previewLabelRef.current?.setMap(null);
      previewLabelRef.current = null;
    };
  }, [map, currentTool, arrowDraft, onArrowDraftStart, onArrowComplete, formatDistance]);

  useEffect(() => {
    if (!map) return;

    arrowClickListenersRef.current.forEach((listener) => listener.remove());
    arrowPolylinesRef.current.forEach((polyline) => polyline.setMap(null));
    arrowLabelsRef.current.forEach((marker) => marker.setMap(null));
    arrowPolylinesRef.current.clear();
    arrowLabelsRef.current.clear();
    arrowClickListenersRef.current.clear();

    arrows.forEach((arrow) => {
      const shouldShowLabel = arrow.showDistanceLabel !== false;
      const polyline = new google.maps.Polyline({
        path: [arrow.start, arrow.end],
        strokeColor: '#db2777',
        strokeOpacity: 1,
        strokeWeight: 4,
        icons: [
          {
            icon: {
              path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
              scale: 3,
              strokeOpacity: 1,
              strokeColor: '#db2777',
              fillColor: '#db2777',
              fillOpacity: 1,
            },
            offset: '0%',
          },
          {
            icon: {
              path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 3,
              strokeOpacity: 1,
              strokeColor: '#db2777',
              fillColor: '#db2777',
              fillOpacity: 1,
            },
            offset: '100%',
          },
        ],
      });

      polyline.setMap(map);
      arrowPolylinesRef.current.set(arrow.id, polyline);

      if (onArrowDelete) {
        const listener = polyline.addListener('click', () => {
          onArrowDelete(arrow.id);
        });
        arrowClickListenersRef.current.set(arrow.id, listener);
      }

      if (shouldShowLabel) {
        const midpoint = {
          lat: (arrow.start.lat + arrow.end.lat) / 2,
          lng: (arrow.start.lng + arrow.end.lng) / 2,
        };

        const labelMarker = new google.maps.Marker({
          position: midpoint,
          label: {
            text: `${formatDistance(arrow.distance)} m`,
            color: '#881337',
            fontWeight: 'bold',
            fontSize: '18px',
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillOpacity: 0,
            strokeOpacity: 0,
          },
          zIndex: 500,
          clickable: true,
        });

        labelMarker.setMap(map);
        arrowLabelsRef.current.set(arrow.id, labelMarker);

        if (onArrowDistanceChange) {
          labelMarker.addListener('click', () => {
            const currentDistance = Number.isFinite(arrow.distance) ? arrow.distance : 0;
            const input = window.prompt('Set arrow distance (metres)', formatDistance(currentDistance));
            if (input === null) return;
            const nextValue = parseFloat(input);
            if (!Number.isFinite(nextValue) || nextValue < 0) {
              alert('Please enter a valid non-negative number.');
              return;
            }
            onArrowDistanceChange(arrow.id, nextValue);
          });
        }
      }
    });
  }, [map, arrows, onArrowDistanceChange, onArrowDelete, formatDistance]);

  const handleLoadError = (error: Error) => {
    setLoadError(error);
    console.error("Error loading Google Maps:", error);
  };


  const handleDrawingManagerLoad = useCallback((drawingMgr: any) => {
    setDrawingManager(drawingMgr);
  }, []);

  const handleWorkzoneComplete = useCallback((polygon: any) => {
    // Remove any previous active polygon being edited
    if (activePolygon) {
      activePolygon.setMap(null);
    }

    const coordinates = polygon.getPath().getArray().map((coord: any) => ({
      lat: coord.lat(),
      lng: coord.lng()
    }));

    // Compute polygon center as fallback if no selectedLocation
    let workzoneLocation = selectedLocation ? {
      lat: selectedLocation.lat(),
      lng: selectedLocation.lng()
    } : null;
    
    if (!workzoneLocation && coordinates.length > 0) {
      // Calculate centroid of polygon
      const sumLat = coordinates.reduce((sum: number, c: any) => sum + c.lat, 0);
      const sumLng = coordinates.reduce((sum: number, c: any) => sum + c.lng, 0);
      workzoneLocation = {
        lat: sumLat / coordinates.length,
        lng: sumLng / coordinates.length
      };
    }

    // Send workzone data to parent immediately
    onWorkzoneSelect({
      polygon: coordinates,
      coordinates, // legacy
      location: workzoneLocation,
      city,
      state: 'SA',  // change to dynamically get the state from the place
      country:'Australia',  // change to dynamically get the country from the place
    });

    // Remove the drawn polygon immediately - the permanent one will be rendered by useEffect
    // This allows the drawing manager to be ready for the next workzone
    polygon.setMap(null);
    setActivePolygon(null);

    // Keep drawing mode active for multiple workzones
    // The drawing manager stays in POLYGON mode so user can immediately draw another
    // User can manually exit by clicking the tool button again
    
    
    // Note: We don't add edit listeners since the polygon is removed immediately
    // The permanent polygons rendered from the workzones array are not editable
    
  }, [activePolygon, onWorkzoneSelect, selectedLocation, city]);
  
  // Render all workzones with mesh pattern
  useEffect(() => {
    if (!map) return;

    // Clear existing workzone polygons
    if (workzone && workzone.length > 0) {
      workzone.forEach((poly: any) => poly?.setMap(null));
    }

    if (!workzones || workzones.length === 0) {
      setWorkzone([]);
      return;
    }

    // Create all workzone polygons with mesh pattern
    const newPolygons = workzones
      .filter(wz => wz.polygon && wz.polygon.length >= 3)
      .map(wz => {
        const polygonCoords = wz.polygon.map((coord: any) => ({ 
          lat: coord.lat, 
          lng: coord.lng 
        }));

        // Create mesh pattern using custom icons
        const meshIcon = {
          path: 'M 0,0 L 10,10 M 10,0 L 0,10', // Diagonal lines for mesh
          strokeColor: '#00E676',
          strokeOpacity: 0.3,
          strokeWeight: 1,
          fillColor: 'transparent',
          fillOpacity: 0,
          scale: 1,
        };

        const polygon = new google.maps.Polygon({
      paths: polygonCoords,
      fillColor: '#00E676',
          fillOpacity: 0.15, // Lighter fill for mesh effect
      strokeColor: '#00E676',
          strokeWeight: 3,
          strokeOpacity: 0.8,
      clickable: false,
      editable: false,
      zIndex: 100,
    });

        polygon.setMap(map);
        return polygon;
      });

    setWorkzone(newPolygons);

    return () => {
      newPolygons.forEach(poly => poly?.setMap(null));
    };
  }, [map, workzones]);

  // previously commented out code
  // const handleRectangleComplete = useCallback((rectangle: any) => {
  //   if (onPrintAreaSelect) {
  //     const bounds = rectangle.getBounds();
  //     if (bounds) {
  //       onPrintAreaSelect({
  //         northeast: { lat: bounds.getNorthEast().lat(), lng: bounds.getNorthEast().lng() },
  //         southwest: { lat: bounds.getSouthWest().lat(), lng: bounds.getSouthWest().lng() },
  //       });
  //     }
  //   }
  //   rectangle.setMap(null); // Remove the rectangle from the map after selection
  //   if (drawingManager) {
  //     drawingManager.setDrawingMode(null);
  //   }
  // }, [onPrintAreaSelect, drawingManager]);

  const handleRectangleComplete = useCallback((rectangle: any) => {
    // Rectangles are only for print area (bounding box)
    if (isDrawingPrintArea && onPrintAreaSelect) {
      const bounds = rectangle.getBounds();
      if (bounds) {
        const area = {
          northeast: { lat: bounds.getNorthEast().lat(), lng: bounds.getNorthEast().lng() },
          southwest: { lat: bounds.getSouthWest().lat(), lng: bounds.getSouthWest().lng() },
        };
        onPrintAreaSelect(area);
        if (typeof onAreaSelected === 'function') {
          onAreaSelected(area);
        }
      }
      
      // Remove rectangle and disable drawing mode (only one print area)
    rectangle.setMap(null);
    if (drawingManager) {
      drawingManager.setDrawingMode(null);
    }
      
    }
  }, [isDrawingPrintArea, onPrintAreaSelect, drawingManager, onAreaSelected]);

  // Wait until we have a valid API key before attempting to load the script
  if (!apiKey) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1">Loading map...</Typography>
      </Paper>
    );
  }

  if (loadError) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">
          Error loading Google Maps. Please check your API key and try again.
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Current API key: {apiKey}
        </Typography>
      </Paper>
    );
  }

  // Helper function to generate InfoWindow content with custom input
  const generateInfoWindowContent = (sign: any, isManualMode: boolean, canRemove: boolean, canResize: boolean) => {
    const currentScale = sign.scale || 1.0;
    const currentPercentage = (currentScale * 100).toFixed(0);
    const currentRotation = sign.rotation !== undefined ? sign.rotation : 0;

    return `
      <div style="
        padding: 8px; 
        font-family: Arial, sans-serif; 
        font-size: 11px; 
        line-height: 1.3;
        width: fit-content;
        max-width: 220px;
      ">
        <div style="
          font-size: 14px; 
          font-weight: bold; 
          color: #333; 
          margin-bottom: 6px;
          text-align: center;
        ">
          ${sign.sign.code_sa}
        </div>
        <div style="display: flex; flex-direction: column; gap: 2px;">
          <div style="color: #666;">
            <strong>ID:</strong> ${sign.id}
          </div>
          <div style="color: #666;">
            <strong>Lane:</strong> ${sign.position?.lane || 'N/A'}
          </div>
          <div style="color: #666;">
            <strong>Ref:</strong> ${sign.position?.ref || 'N/A'}
          </div>
          <div style="color: #666; font-size: 10px;">
            ${sign.lat.toFixed(4)}, ${sign.lng.toFixed(4)}
          </div>
        </div>
        ${isManualMode && canResize ? `
          <div style="
            margin-top: 8px;
            padding: 8px;
            background-color: #f5f5f5;
            border-radius: 3px;
          ">
            <div style="
              font-weight: bold;
              color: #555;
              margin-bottom: 6px;
              font-size: 10px;
              text-align: center;
            ">
              Sign Size: ${currentPercentage}%
            </div>
            <div style="display: flex; gap: 4px; margin-bottom: 6px; align-items: center;">
              <input 
                type="number"
                id="scale-input-${sign.id}"
                value="${currentPercentage}"
                min="50"
                max="300"
                step="25"
                style="
                  width: 60px;
                  padding: 4px;
                  border: 1px solid #ccc;
                  border-radius: 3px;
                  font-size: 11px;
                  text-align: center;
                "
                onkeypress="if(event.key === 'Enter') window.applyCustomScale('${sign.id}')"
              />
              <span style="color: #666; font-size: 11px;">%</span>
              <button 
                onclick="window.applyCustomScale('${sign.id}')"
                style="
                  padding: 4px 8px;
                  background-color: #4CAF50;
                  color: white;
                  border: none;
                  border-radius: 3px;
                  font-size: 10px;
                  cursor: pointer;
                  flex: 1;
                "
                onmouseover="this.style.backgroundColor='#45a049'"
                onmouseout="this.style.backgroundColor='#4CAF50'"
              >
                Apply
              </button>
            </div>
            <div style="display: flex; gap: 4px; justify-content: center;">
              <button 
                onclick="window.resizeSign && window.resizeSign('${sign.id}', ${Math.max(0.5, currentScale - 0.25).toFixed(2)})"
                style="
                  padding: 4px 12px;
                  background-color: #2196F3;
                  color: white;
                  border: none;
                  border-radius: 3px;
                  font-size: 14px;
                  cursor: pointer;
                  font-weight: bold;
                  min-width: 32px;
                "
                onmouseover="this.style.backgroundColor='#1976D2'"
                onmouseout="this.style.backgroundColor='#2196F3'"
                ${currentScale <= 0.5 ? 'disabled style="opacity: 0.5; cursor: not-allowed;"' : ''}
              >
                −
              </button>
              <button 
                onclick="window.resizeSign && window.resizeSign('${sign.id}', 1.0)"
                style="
                  padding: 4px 8px;
                  background-color: #757575;
                  color: white;
                  border: none;
                  border-radius: 3px;
                  font-size: 9px;
                  cursor: pointer;
                  min-width: 40px;
                "
                onmouseover="this.style.backgroundColor='#616161'"
                onmouseout="this.style.backgroundColor='#757575'"
              >
                Reset
              </button>
              <button 
                onclick="window.resizeSign && window.resizeSign('${sign.id}', ${Math.min(3.0, currentScale + 0.25).toFixed(2)})"
                style="
                  padding: 4px 12px;
                  background-color: #2196F3;
                  color: white;
                  border: none;
                  border-radius: 3px;
                  font-size: 14px;
                  cursor: pointer;
                  font-weight: bold;
                  min-width: 32px;
                "
                onmouseover="this.style.backgroundColor='#1976D2'"
                onmouseout="this.style.backgroundColor='#2196F3'"
                ${currentScale >= 3.0 ? 'disabled style="opacity: 0.5; cursor: not-allowed;"' : ''}
              >
                +
              </button>
            </div>
          </div>
        ` : ''}
        ${isManualMode ? `
          <div style="
            margin-top: 8px;
            padding: 8px;
            background-color: #fff3e0;
            border-radius: 3px;
          ">
            <div style="
              font-weight: bold;
              color: #555;
              margin-bottom: 6px;
              font-size: 10px;
              text-align: center;
            ">
              Rotation: ${currentRotation.toFixed(0)}°
            </div>
            <div style="display: flex; gap: 4px; justify-content: center;">
              <button 
                onclick="window.rotateSign && window.rotateSign('${sign.id}', ${((currentRotation - 15) + 360) % 360})"
                style="
                  padding: 4px 12px;
                  background-color: #FF9800;
                  color: white;
                  border: none;
                  border-radius: 3px;
                  font-size: 14px;
                  cursor: pointer;
                  font-weight: bold;
                  min-width: 32px;
                "
                onmouseover="this.style.backgroundColor='#F57C00'"
                onmouseout="this.style.backgroundColor='#FF9800'"
              >
                ↺
              </button>
              <button 
                onclick="window.rotateSign && window.rotateSign('${sign.id}', 0)"
                style="
                  padding: 4px 8px;
                  background-color: #757575;
                  color: white;
                  border: none;
                  border-radius: 3px;
                  font-size: 9px;
                  cursor: pointer;
                  min-width: 40px;
                "
                onmouseover="this.style.backgroundColor='#616161'"
                onmouseout="this.style.backgroundColor='#757575'"
              >
                Reset
              </button>
              <button 
                onclick="window.rotateSign && window.rotateSign('${sign.id}', ${(currentRotation + 15) % 360})"
                style="
                  padding: 4px 12px;
                  background-color: #FF9800;
                  color: white;
                  border: none;
                  border-radius: 3px;
                  font-size: 14px;
                  cursor: pointer;
                  font-weight: bold;
                  min-width: 32px;
                "
                onmouseover="this.style.backgroundColor='#F57C00'"
                onmouseout="this.style.backgroundColor='#FF9800'"
              >
                ↻
              </button>
            </div>
          </div>
        ` : ''}
        ${isManualMode && canRemove ? `
          <button 
            onclick="window.removeSign && window.removeSign('${sign.id}')"
            style="
              width: 100%;
              padding: 6px 12px;
              background-color: #f44336;
              color: white;
              border: none;
              border-radius: 3px;
              font-size: 11px;
              cursor: pointer;
              font-weight: bold;
              margin-top: 6px;
            "
            onmouseover="this.style.backgroundColor='#d32f2f'"
            onmouseout="this.style.backgroundColor='#f44336'"
          >
            Remove
          </button>
        ` : ''}
      </div>
    `;
  };

  // Helper function to get composite sign icon
  const getCompositeSignIcon = (composite: any, scale: number = 1.0, rotation: number = 0) => {
    if (!composite || !composite.components) {
      console.warn('⚠️ Composite sign missing data:', composite);
      return {
        path: window.google?.maps.SymbolPath.CIRCLE,
        scale: 8 * scale,
        fillColor: '#9C27B0', // Purple for composites
        fillOpacity: 0.9,
        strokeColor: '#000000',
        strokeWeight: 2,
      };
    }

    // Check if signLibrary is available
    if (!signLibrary || Object.keys(signLibrary).length === 0) {
      console.warn('⚠️ Sign library not loaded yet for composite icon');
      return {
        path: window.google?.maps.SymbolPath.CIRCLE,
        scale: 8 * scale,
        fillColor: '#9C27B0',
        fillOpacity: 0.9,
        strokeColor: '#000000',
        strokeWeight: 2,
      };
    }

    try {
      const containerWidth = composite.canvasWidth;
      const containerHeight = composite.canvasHeight;

      // Build composite SVG using DOM for proper color preservation
      const containerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      containerSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      containerSvg.setAttribute('width', String(containerWidth));
      containerSvg.setAttribute('height', String(containerHeight));
      containerSvg.setAttribute('viewBox', `0 0 ${containerWidth} ${containerHeight}`);
      
      // Add background/frame
      const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bgRect.setAttribute('x', '0');
      bgRect.setAttribute('y', '0');
      bgRect.setAttribute('width', String(containerWidth));
      bgRect.setAttribute('height', String(containerHeight));
      bgRect.setAttribute('fill', '#f5f5f5');
      bgRect.setAttribute('stroke', '#333');
      bgRect.setAttribute('stroke-width', '4');
      containerSvg.appendChild(bgRect);

      // Add each component sign
      composite.components.forEach((component: any, index: number) => {
        const signData = signLibrary?.[component.signCode.toLowerCase()];
        if (!signData?.svg) {
          console.warn(`⚠️ Sign ${component.signCode} not found in library`);
          return;
        }

        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(signData.svg, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        if (svgElement) {
          // Apply unique IDs and CSS classes to prevent conflicts
          const uniquePrefix = `map-${composite.id || 'comp'}-${component.id || component.signCode}-${index}`;
          applyUniqueIds(svgElement, uniquePrefix);

          const viewBox = svgElement.getAttribute('viewBox') || '0 0 40 40';
          const [vbX, vbY, vbWidth, vbHeight] = viewBox.split(' ').map(Number);
          
          // Calculate scale to fit component size
          const scaleX = component.width / vbWidth;
          const scaleY = component.height / vbHeight;
          
          // Build transform with translation and scaling
          const transform = `translate(${component.x}, ${component.y}) scale(${scaleX}, ${scaleY}) translate(${-vbX}, ${-vbY})`;
          
          // Create a group with transform
          const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          g.setAttribute('transform', transform);
          
          // Separate style/defs from graphical elements
          const children = Array.from(svgElement.children);
          for (const child of children) {
            const clonedChild = child.cloneNode(true) as Element;
            const tagName = clonedChild.tagName.toLowerCase();
            
            // Style and defs go to the root SVG, not in the transformed group
            if (tagName === 'style' || tagName === 'defs') {
              containerSvg.appendChild(clonedChild);
            } else {
              // Graphical elements go in the transformed group
              g.appendChild(clonedChild);
            }
          }
          
          // Only append the group if it has children
          if (g.children.length > 0) {
            containerSvg.appendChild(g);
          }
        }
      });

      // Serialize the DOM element to string
      const serializer = new XMLSerializer();
      let compositeSvg = serializer.serializeToString(containerSvg);

      // Calculate diagonal for rotation support
      const diagonal = Math.sqrt(containerWidth * containerWidth + containerHeight * containerHeight);
      const newViewBoxSize = Math.ceil(diagonal * 1.1);

      // Apply rotation if needed
      if (rotation !== 0) {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(compositeSvg, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        if (svgElement) {
          const centerX = containerWidth / 2;
          const centerY = containerHeight / 2;

          const existingContent = Array.from(svgElement.children);
          const g = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
          g.setAttribute('transform', `rotate(${rotation} ${centerX} ${centerY})`);

          existingContent.forEach(child => {
            g.appendChild(child.cloneNode(true));
          });

          while (svgElement.firstChild) {
            svgElement.removeChild(svgElement.firstChild);
          }
          svgElement.appendChild(g);

          const newVbX = centerX - newViewBoxSize / 2;
          const newVbY = centerY - newViewBoxSize / 2;
          svgElement.setAttribute('viewBox', `${newVbX} ${newVbY} ${newViewBoxSize} ${newViewBoxSize}`);
          svgElement.setAttribute('width', `${newViewBoxSize}`);
          svgElement.setAttribute('height', `${newViewBoxSize}`);

          compositeSvg = new XMLSerializer().serializeToString(svgElement);
        }
      }

      const baseSize = 64; // Larger base for composites
      const scaledSize = baseSize * scale;
      const anchorPoint = scaledSize / 2;

      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(compositeSvg)}`,
        scaledSize: new window.google.maps.Size(scaledSize, scaledSize),
        anchor: new window.google.maps.Point(anchorPoint, anchorPoint),
      };
    } catch (error) {
      console.error('Error creating composite icon:', error);
      return {
        path: window.google?.maps.SymbolPath.CIRCLE,
        scale: 8 * scale,
        fillColor: '#9C27B0',
        fillOpacity: 0.9,
        strokeColor: '#000000',
        strokeWeight: 2,
      };
    }
  };

  // Helper function to get sign icon with optional scaling and rotation
  const getSignIcon = (signCode: string, scale: number = 1.0, rotation: number = 0) => {
    // Default fallback icon
    const fallbackIcon = {
      path: window.google?.maps.SymbolPath.CIRCLE,
      scale: 8 * scale,
      fillColor: '#FFD700', // Gold color for signs
      fillOpacity: 0.9,
      strokeColor: '#000000',
      strokeWeight: 2,
    };

    if (!signLibrary || !signCode) {
      return fallbackIcon;
    }

    // Try case-insensitive lookup (sign library uses lowercase keys)
    const normalizedSignCode = signCode.toLowerCase();
    const signData = signLibrary[normalizedSignCode] || signLibrary[signCode];
    
    if (!signData || !signData.svg) {
      return fallbackIcon;
    }

    const svg = signData.svg;

    try {
      // Calculate scaled dimensions (base size 32x32)
      const baseSize = 32;
      const scaledSize = baseSize * scale;
      const anchorPoint = scaledSize / 2;

      // Parse SVG to apply rotation
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svg, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');
      
      if (svgElement) {
        // Get original viewBox or create one based on width/height attributes
        let viewBoxAttr = svgElement.getAttribute('viewBox');
        let vbX = 0, vbY = 0, vbWidth = 40, vbHeight = 40;
        
        if (viewBoxAttr) {
          [vbX, vbY, vbWidth, vbHeight] = viewBoxAttr.split(/\s+/).map(Number);
        } else {
          // Try to get from width/height attributes
          const widthAttr = svgElement.getAttribute('width');
          const heightAttr = svgElement.getAttribute('height');
          if (widthAttr) vbWidth = parseFloat(widthAttr);
          if (heightAttr) vbHeight = parseFloat(heightAttr);
        }
        
        // Calculate the diagonal of the original bounding box
        // This is the minimum size needed to fit the sign at any rotation
        const diagonal = Math.sqrt(vbWidth * vbWidth + vbHeight * vbHeight);
        
        // Create a square viewBox based on the diagonal
        // This ensures the sign won't be clipped at any rotation angle
        const newViewBoxSize = Math.ceil(diagonal * 1.1); // Add 10% padding for safety
        
        // Calculate the center of the original viewBox
        const originalCenterX = vbX + vbWidth / 2;
        const originalCenterY = vbY + vbHeight / 2;
        
        // Calculate new viewBox origin to center the original content
        const newVbX = originalCenterX - newViewBoxSize / 2;
        const newVbY = originalCenterY - newViewBoxSize / 2;
        
        // The rotation center should be the original content center
        const rotationCenterX = originalCenterX;
        const rotationCenterY = originalCenterY;
        
        // Wrap existing content in a group with rotation transform
        const existingContent = Array.from(svgElement.children);
        const g = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('transform', `rotate(${rotation} ${rotationCenterX} ${rotationCenterY})`);
        
        existingContent.forEach(child => {
          g.appendChild(child.cloneNode(true));
        });
        
        // Clear svg and add rotated group
        while (svgElement.firstChild) {
          svgElement.removeChild(svgElement.firstChild);
        }
        svgElement.appendChild(g);
        
        // Set the new, larger square viewBox
        const newViewBox = `${newVbX} ${newVbY} ${newViewBoxSize} ${newViewBoxSize}`;
        svgElement.setAttribute('viewBox', newViewBox);
        svgElement.setAttribute('width', `${newViewBoxSize}`);
        svgElement.setAttribute('height', `${newViewBoxSize}`);
        
        const rotatedSvg = new XMLSerializer().serializeToString(svgElement);
        
        const icon = {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(rotatedSvg)}`,
          scaledSize: new window.google.maps.Size(scaledSize, scaledSize),
          anchor: new window.google.maps.Point(anchorPoint, anchorPoint),
        };
        
        return icon;
      }

      // Fallback if parsing fails
      const icon = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
          svg.replace('<svg', '<svg width="40" height="40"')
        )}`,
        scaledSize: new window.google.maps.Size(scaledSize, scaledSize),
        anchor: new window.google.maps.Point(anchorPoint, anchorPoint),
      };
      
      return icon;
    } catch (error) {
      console.error('Error creating SVG icon:', error);
      return fallbackIcon;
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!map || !onSignAdd || !isManualMode) {
      return;
    }

    // Parse drag data
    let signCode = e.dataTransfer.getData('signCode');
    let composite: any = null;
    
    // Try parsing from 'application/json' for more complex data
      const json = e.dataTransfer.getData('application/json');
    if (json) {
      try {
        const data = JSON.parse(json);
        
        // Check if it's a composite sign
        if (data.type === 'composite' && data.composite) {
          signCode = 'COMPOSITE';
          composite = data.composite;
        } else if (data && data.code) {
          signCode = data.code;
        }
      } catch (e) {
        console.error('Failed to parse drop data:', e);
      }
    }

    if (!signCode) return;

    // Get the map container bounds
    const mapDiv = map.getDiv();
    const rect = mapDiv.getBoundingClientRect();
    
    const pixelX = e.clientX - rect.left;
    const pixelY = e.clientY - rect.top;
    
    
    // Always default signs to 0° rotation (north-facing)
    const defaultRotation = 0;

    const finalizePlacement = (latLng: google.maps.LatLng) => {
      
      // If it's a composite sign, pass the composite data
      if (composite) {
        onSignAdd(signCode, { lat: latLng.lat(), lng: latLng.lng() }, defaultRotation, composite);
      } else {
        onSignAdd(signCode, { lat: latLng.lat(), lng: latLng.lng() }, defaultRotation);
      }
      
    setIsDraggedOver(false);
    };

    const overlay = new window.google.maps.OverlayView();

    const convert = () => {
      const projection = overlay.getProjection();
      if (!projection) {
        console.warn('Projection not ready; waiting for next frame');
        requestAnimationFrame(convert);
        return;
      }

      const point = new window.google.maps.Point(pixelX, pixelY);
      const latLng = projection.fromContainerPixelToLatLng(point);
      overlay.setMap(null);

      if (!latLng) {
        console.error('Failed to convert point to LatLng');
        setIsDraggedOver(false);
        return;
      }

      finalizePlacement(latLng);
    };

    overlay.onAdd = () => {
      requestAnimationFrame(convert);
    };
    overlay.draw = function() {};
    overlay.onRemove = function() {};
    overlay.setMap(map);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer && isManualMode) {
      e.dataTransfer.dropEffect = 'copy';
    } else if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isManualMode && e.dataTransfer.types.includes('signCode')) {
      setIsDraggedOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the main container
    if (e.currentTarget === e.target) {
      setIsDraggedOver(false);
    }
  };
  

  return (
    <Paper 
        elevation={3} 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          position: 'relative',
          ...(isManualMode && isDraggedOver && {
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            '&::before': {
              content: '"Drop sign here"',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(76, 175, 80, 0.9)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: 'bold',
              zIndex: 10000,
              pointerEvents: 'none'
            }
          })
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
    >
      <LoadScript
        googleMapsApiKey={apiKey}
        libraries={libraries}
        onError={handleLoadError}
      >
        {/* Styled Google Maps Autocomplete Search Bar */}
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 20,
            pointerEvents: isDrawingPrintArea ? 'none' : 'auto',
            width: 420,
            maxWidth: '90vw',
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            
           
            boxShadow: '0px 18px 40px rgba(15, 23, 42, 0.22)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                overflow: 'hidden',  
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx:0.5
              }}
            >
              <img
                src={logo}     
                alt="Logo"
                style={{
                  width: '80%',
                  height: '80%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {/* wrapper div provides width control */}
            <Box sx={{ width: '100%' }}>
              <Autocomplete
                onLoad={(instance) => {
                  autocompleteRef.current = instance;
                }}
                onPlaceChanged={handlePlaceSelect}
              >
                <InputBase
                  inputRef={inputRef}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                  placeholder="Search for worksite.."
                  fullWidth
                  sx={{
                    flex: 1,
                    width: '100%',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#111827',
                    '&::placeholder': {
                      color: '#9ca3af',
                    },
                  }}
                />
              </Autocomplete>
            </Box>
          </Box>

          <IconButton
            onClick={handleSearchSubmit}
            sx={{
              width: 48,
              height: 48,
              backgroundColor: 'transparent',
              color: 'black',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <img
              src="/images/search-alt-svgrepo-com.svg"    // path is relative to public/
              alt="Search"
              style={{ width: 27, height: 27 }} // matches the old fontSize: 32
            />
        </IconButton>
        </Paper>
        <Box sx={{ flexGrow: 1, height: '100%' }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            options={mapOptions}
            onLoad={handleMapLoad}
          >
            {/* Show marker at selected location */}
            {selectedLocation && (
              <Marker
                position={selectedLocation}
                icon={{
                  path: window.google?.maps.SymbolPath.CIRCLE,
                  scale: 10,
                  fillColor: '#4285F4',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 2,
                }}
                clickable={false} // Disable click to prevent default InfoWindow
              />
            )}

           
             {isGoogleReady && (
             <DrawingManager
              onLoad={handleDrawingManagerLoad}
              onPolygonComplete={handleWorkzoneComplete}
              onRectangleComplete={handleRectangleComplete}
              options={{
                drawingControl: false,
                drawingControlOptions: {
                  position: window.google && window.google.maps ? window.google.maps.ControlPosition.BOTTOM_CENTER : undefined,
                  drawingModes: [
                    window.google?.maps.drawing.OverlayType.POLYGON,
                    window.google?.maps.drawing.OverlayType.RECTANGLE,
                  ],
                },
                polygonOptions: {
                  fillColor: "#00FF00",  // Green for workzones
                  fillOpacity: 0.3,
                  strokeWeight: 2,
                  strokeColor: "#00FF00",
                  clickable: false,
                  editable: false,
                  zIndex: 100,
                },
                rectangleOptions: {
                  fillColor: "#FF0000",  // Red for print areas
                  fillOpacity: 0.3,
                  strokeWeight: 2,
                  strokeColor: "#FF0000",
                  clickable: false,
                  editable: false,
                  zIndex: 100,
                },
              }}
            />
            )}
            
            {/* Render sign placement markers */}
            {signPlacement?.signs?.map((sign: any) => {
              const rotation = sign.rotation !== undefined ? sign.rotation : 0;
              const isComposite = sign.sign.code_sa === 'COMPOSITE' && sign.composite;
              
              // Log composite sign rendering
              if (isComposite) {
              }
              
              // Use composite icon for composite signs, regular icon for normal signs
              const icon = isComposite
                ? getCompositeSignIcon(sign.composite, sign.scale || 1.0, rotation)
                : getSignIcon(sign.sign.code_sa, sign.scale || 1.0, rotation);
              
              return sign?.id && sign?.sign?.code_sa ? (
                <Marker
                  key={sign.id}
                  position={{ lat: sign.lat, lng: sign.lng }}
                  icon={icon}
                  draggable={isManualMode}
                  onLoad={(marker) => {
                    // Store marker reference
                    markersRef.current.set(sign.id, marker);
                    
                    // Create InfoWindow (will be populated when clicked)
                    const infoWindow = new window.google.maps.InfoWindow({
                      maxWidth: 240,
                    });
                    
                    // Store InfoWindow reference
                    infoWindowsRef.current.set(sign.id, infoWindow);
                    
                    // Store reference to current sign for click handler
                    const signRef = sign;
                    
                    // Add click listener to show InfoWindow with fresh content
                    marker.addListener('click', () => {
                      const infoContent = generateInfoWindowContent(
                        signRef, 
                        isManualMode, 
                        !!onSignRemove, 
                        !!onSignResize
                      );
                      
                      infoWindow.setContent(infoContent);
                      infoWindow.open(map, marker);
                      
                      // Track that this InfoWindow is now open
                      openInfoWindowRef.current = signRef.id;
                    });
                    
                    // Track when InfoWindow closes
                    infoWindow.addListener('closeclick', () => {
                      if (openInfoWindowRef.current === signRef.id) {
                        openInfoWindowRef.current = null;
                      }
                    });
                  }}
                  onDragEnd={(e) => {
                    if (onSignMove && e.latLng) {
                      onSignMove(sign.id, { lat: e.latLng.lat(), lng: e.latLng.lng() });
                    }
                  }}
                  onUnmount={() => {
                    // Clean up references when marker is removed
                    infoWindowsRef.current.delete(sign.id);
                    markersRef.current.delete(sign.id);
                    if (openInfoWindowRef.current === sign.id) {
                      openInfoWindowRef.current = null;
                    }
                  }}
                />
              ) : null;
            })}

            {/* Render Cones from Multi-Agent System */}
            {cones?.map((cone: any) => {
              return cone?.id && Number.isFinite(cone.lat) && Number.isFinite(cone.lng) ? (
                <Marker
                  key={cone.id}
                  position={{ lat: cone.lat, lng: cone.lng }}
                  icon={{
                    path: window.google?.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: '#FF6B35',
                    fillOpacity: 0.8,
                    strokeColor: '#D84315',
                    strokeWeight: 2,
                  }}
                  title="Traffic Cone"
                />
              ) : null;
            })}
          </GoogleMap>
          
          {/* Road Tool Overlay - renders vector road geometry */}
          {map && (
            <RoadToolOverlay
              workzone={workzones}
              map={map}
              geometry={roadToolGeometry}
              visible={roadToolVisible && isProMode}
              editable={isProMode}
            />
          )}
        </Box>
      </LoadScript>
    </Paper>
  );
}; 